import * as Sentry from "@sentry/react";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

export function initSentry() {
  if (!sentryDsn) return;

  /**
   * PII / confidentiality note (CA firm / financial gateway):
   * This application handles highly sensitive financial data (e.g., PAN, TAN, GST numbers),
   * government portal credentials, and client financial information. Sending default PII to any
   * third-party monitoring service increases confidentiality and compliance risk.
   *
   * `sendDefaultPii` controls whether Sentry automatically attaches personally identifiable
   * information (PII) such as:
   * - user emails / user identifiers (when set via Sentry.setUser / scope)
   * - IP addresses (depending on SDK/platform defaults)
   * - user context and tags that may contain identifiers
   * - breadcrumbs that can inadvertently include sensitive inputs (e.g., form fields)
   *
   * For professional confidentiality and data-protection expectations, this is disabled by default
   * and is forced OFF in production as a defense-in-depth safeguard against misconfiguration.
   *
   * Only consider enabling in isolated development environments for targeted debugging, and
   * never for production for this use case.
   */
  const shouldSendPii =
    import.meta.env.MODE !== "production" &&
    import.meta.env.VITE_SENTRY_SEND_PII === "true";

  if (import.meta.env.DEV && shouldSendPii) {
    // eslint-disable-next-line no-console
    console.warn(
      "[Sentry] WARNING: PII sending is enabled (VITE_SENTRY_SEND_PII=true). Do not use this in production."
    );
  }

  const enableReplay = import.meta.env.MODE !== "production";

  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    // Force-disable PII in production regardless of env var to prevent accidental leaks.
    sendDefaultPii: shouldSendPii,
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay can capture sensitive UI interactions; keep it disabled in production
      // for financial workflows unless explicitly reviewed and approved.
      ...(enableReplay ? [Sentry.replayIntegration()] : []),
    ],
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/caaralimatti\.com/i,
    ],
    // Breadcrumbs may include form inputs or other sensitive values; sanitize defensively.
    beforeBreadcrumb(breadcrumb) {
      if (!breadcrumb) return breadcrumb;

      const data = breadcrumb.data ? { ...breadcrumb.data } : undefined;
      if (data) {
        for (const key of Object.keys(data)) {
          if (/(password|pass|secret|token|otp|pan|tan|gst)/i.test(key)) {
            data[key] = "[Filtered]";
          }
        }
      }

      return {
        ...breadcrumb,
        data,
      };
    },
    // Disable Replay in production; even in non-production, keep rates conservative by default.
    replaysSessionSampleRate: enableReplay ? (import.meta.env.DEV ? 1.0 : 0.05) : 0.0,
    replaysOnErrorSampleRate: enableReplay ? 1.0 : 0.0,
  });
}

export const captureException = Sentry.captureException;
